'use client';
import { useState, useEffect } from "react";
import * as Dialog from '@radix-ui/react-dialog';
import clsx from "clsx";
import { Inter } from "next/font/google";
import { useRouter } from 'next/navigation';
import { identifyUser } from "@/components/FullStory";

const inter = Inter({ 
    subsets: ["latin"],
    weight: ["600"],
    display: "swap"
});

export default function CoachLoginPage() {
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const router = useRouter();

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setShowErrorModal(false);
        setErrorMessage('');
        
        const formData = new FormData(e.currentTarget);
        
        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                body: formData,
                redirect: 'follow'
            });
            // console.log('Login response:', response);
            if (response.ok) {
                // Get the redirect URL from the response
                const redirectUrl = response.url;
                
                // Get user ID from the ID endpoint
                const idResponse = await fetch('/api/id');
                if (idResponse.ok) {
                    const { id, coach_user_id, type } = await idResponse.json();
                    // Identify user in FullStory
                    identifyUser(coach_user_id , 'coach');
                }

                if (redirectUrl) {
                    router.push(redirectUrl);
                } else {
                    router.push('/meeting');
                }
                return;
            }

            const errorText = await response.text();
            if (errorText === 'Coach not found') {
                setErrorMessage('Incorrect username or password');
                setShowErrorModal(true);
            } else {
                setErrorMessage('An error occurred during login');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Login error:', error);
            setErrorMessage('An error occurred during login');
            setShowErrorModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="inline-flex flex-col justify-start items-center gap-10 w-full px-4 sm:px-0">
            <div className="flex flex-col gap-2">
                <h3 className={clsx(
                    "text-center justify-start text-3xl font-semibold tracking-tight leading-tight",
                    inter.className
                )}>Login to your account</h3>
                <h4 className="text-base font-normal leading-tight text-[#35404B]">You can log in using your Reframe coach credentials.</h4>
            </div>
            <form className="self-stretch" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-6">
                    <input 
                        id="username" 
                        type="text" 
                        name="username" 
                        className="text-sm text-black sm:text-base placeholder-gray-500 px-4 rounded-lg border border-gray-400 w-full py-4 focus:outline-none focus:border-blue-400" 
                        placeholder="Username" 
                        disabled={isLoading}
                    />
                    <input 
                        id="password" 
                        type="password" 
                        name="password" 
                        className="text-sm text-black sm:text-base placeholder-gray-500 px-4 rounded-lg border border-gray-400 w-full py-4 focus:outline-none focus:border-blue-400" 
                        placeholder="Password" 
                        disabled={isLoading}
                    />
                </div>

                <div className="flex w-full mt-12">
                    <input id="role" type="hidden" name="role" value="host" />
                    <button 
                        type="submit" 
                        className={clsx(
                            "w-full bg-[#2B3A67] text-white text-xl font-semibold py-4 rounded-full transition duration-150 ease-in",
                            isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#232F52]"
                        )}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center">
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : "Log In"}
                    </button>
                </div>
            </form>

            <Dialog.Root open={showErrorModal} onOpenChange={setShowErrorModal}>
                <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 bg-black/30" />
                    <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[450px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none">
                        <Dialog.Title className="m-0 text-[17px] font-medium text-gray-900">
                            Login Failed
                        </Dialog.Title>
                        <Dialog.Description className="mt-4 mb-5 text-[15px] leading-normal text-gray-600">
                            {errorMessage}
                        </Dialog.Description>
                        <div className="mt-[25px] flex justify-end">
                            <Dialog.Close asChild>
                                <button className="inline-flex h-[35px] items-center justify-center rounded-[4px] bg-blue-600 px-[15px] font-medium leading-none text-white hover:bg-blue-700 focus:shadow-[0_0_0_2px] focus:shadow-blue-700 focus:outline-none">
                                    Try Again
                                </button>
                            </Dialog.Close>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </div>
    )
}